import { http } from '@/api/http';
import type { JsonPatchOp } from '@/features/admin-consent-types/endpoints';

/**
 * Wire DTO for a consent record. Field names are **snake_case** and the
 * status enum is **UPPERCASE** — this matches what the BE's ObjectMapper
 * actually accepts (Jackson SNAKE_CASE naming strategy), not the shape
 * Springdoc reports in the OpenAPI export. Sending camelCase gets rejected
 * with "consentDTO.consentType must not be blank" because Jackson maps the
 * missing `consent_type` key to a null Java field.
 *
 * Hand-declared here because the ai-hub OpenAPI export still describes the
 * pre-PATCH shape — see PTV2-1209.
 */
export interface ConsentDto {
  readonly id?: string;
  readonly person_guid?: string;
  readonly consent_type: string;
  readonly status: ConsentWireStatus;
  readonly signed_at?: string | null;
  readonly signed_by?: string | null;
  readonly notes?: string | null;
  readonly document_ref?: string | null;
  readonly created_at?: string;
  readonly created_by?: string;
  readonly updated_at?: string;
  readonly updated_by?: string;
  readonly index_number?: number;
}

/** Lowercase status enum on the wire — matches the Jackson-registered
 *  Java enum names (see `ConsentStatus` on the BE). UPPERCASE is rejected. */
export type ConsentWireStatus = 'pending' | 'signed' | 'declined' | 'withdrawn';

export const consentsApi = {
  /** GET /persons/{guid}/consents — live consents in add-order. */
  getConsents: (
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<ConsentDto[]> =>
    http.get<ConsentDto[]>(
      `/persons/${encodeURIComponent(guid)}/consents`,
      options,
    ),

  /**
   * POST /persons/{guid}/consents — create.
   * 409 if a live consent of the same type exists; 422 if `consent_type` is
   * not an active catalogue entry.
   */
  addConsent: (
    guid: string,
    body: ConsentDto,
    options?: { signal?: AbortSignal },
  ): Promise<ConsentDto> =>
    http.post<ConsentDto>(
      `/persons/${encodeURIComponent(guid)}/consents`,
      body,
      options,
    ),

  /**
   * PATCH /persons/{guid}/consents/{id} — RFC 6902 JSON-Patch over
   * `consent_type`, `status`, `signed_at`, `signed_by`, `notes`, `document_ref`.
   * Merged `status=WITHDRAWN` is 422 — use `withdrawConsent` for that.
   */
  patchConsent: (
    guid: string,
    id: string,
    ops: readonly JsonPatchOp[],
    options?: { signal?: AbortSignal },
  ): Promise<ConsentDto> =>
    http.patch<ConsentDto>(
      `/persons/${encodeURIComponent(guid)}/consents/${encodeURIComponent(id)}`,
      ops,
      {
        headers: { 'Content-Type': 'application/json-patch+json' },
        ...(options?.signal !== undefined ? { signal: options.signal } : {}),
      },
    ),

  /** POST /persons/{guid}/consents/{id}/withdraw — SIGNED → WITHDRAWN. */
  withdrawConsent: (
    guid: string,
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<ConsentDto> =>
    http.post<ConsentDto>(
      `/persons/${encodeURIComponent(guid)}/consents/${encodeURIComponent(id)}/withdraw`,
      undefined,
      options,
    ),

  /** POST /persons/{guid}/consents/{id}/reinstate — WITHDRAWN → SIGNED. */
  reinstateConsent: (
    guid: string,
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<ConsentDto> =>
    http.post<ConsentDto>(
      `/persons/${encodeURIComponent(guid)}/consents/${encodeURIComponent(id)}/reinstate`,
      undefined,
      options,
    ),

  /** DELETE /persons/{guid}/consents/{id} — 204 soft-delete. */
  deleteConsent: (
    guid: string,
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(
      `/persons/${encodeURIComponent(guid)}/consents/${encodeURIComponent(id)}`,
      options,
    ),
};
